import { getMessaging, getToken } from "firebase/messaging";
import { createLogger } from "@progressive-development/pd-utils";
//#region src/FirebaseMessagingProvider.ts
/** LocalStorage key for persisting the notification token */
var NOTIFICATION_TOKEN_KEY = "pd.spa.helper.notification.token";
/**
* Firebase Messaging Provider for Push Notifications
*
* This provider handles FCM (Firebase Cloud Messaging) registration and token management.
* It is intentionally decoupled from the store - the host app decides what to do with tokens.
*
* @example
* ```typescript
* const provider = createFirebaseProvider({
*   firebaseConfig: { ... },
*   messagingConfig: { vapidKey: "your-vapid-key" }
* });
*
* // In your Service Worker registration handler:
* const result = await provider.messaging.registerDevice(registration);
* if (result.token && result.isNewToken) {
*   // Send token to your backend
*   await myApi.updateDeviceToken(result.token);
*   // Persist locally
*   provider.messaging.storeToken(result.token);
* }
* ```
*/
var FirebaseMessagingProvider = class {
	constructor(app, config) {
		this.config = config;
		this.messaging = getMessaging(app);
		this.logger = createLogger("FirebaseMessaging");
	}
	/**
	* Register the device for push notifications
	*
	* This method:
	* 1. Requests notification permission if not yet granted
	* 2. Retrieves the FCM token
	* 3. Compares with stored token to determine if it's new
	*
	* @param registration - The ServiceWorkerRegistration instance
	* @returns Registration result with token and status
	*/
	async registerDevice(registration) {
		this.logger.info("Starting device registration for push notifications");
		if (Notification.permission === "default") {
			this.logger.info("Requesting notification permission from user");
			await Notification.requestPermission();
		}
		if (Notification.permission === "denied") {
			this.logger.warn("Notification permission denied by user");
			return {
				token: null,
				isNewToken: false,
				permission: "denied"
			};
		}
		if (Notification.permission === "granted") try {
			const currentToken = await getToken(this.messaging, {
				vapidKey: this.config.vapidKey,
				serviceWorkerRegistration: registration
			});
			if (currentToken) {
				const isNewToken = this.getStoredToken() !== currentToken;
				if (isNewToken) this.logger.info("New FCM token received");
				else this.logger.info("FCM token unchanged");
				return {
					token: currentToken,
					isNewToken,
					permission: "granted"
				};
			} else {
				this.logger.warn("No FCM token received from Firebase");
				return {
					token: null,
					isNewToken: false,
					permission: "granted"
				};
			}
		} catch (error) {
			this.logger.error("Failed to get FCM token", error);
			throw error;
		}
		return {
			token: null,
			isNewToken: false,
			permission: Notification.permission
		};
	}
	/**
	* Get the stored notification token from localStorage
	*/
	getStoredToken() {
		return localStorage.getItem(NOTIFICATION_TOKEN_KEY);
	}
	/**
	* Store the notification token in localStorage
	*/
	storeToken(token) {
		localStorage.setItem(NOTIFICATION_TOKEN_KEY, token);
		this.logger.info("FCM token stored in localStorage");
	}
	/**
	* Clear the stored notification token
	*/
	clearStoredToken() {
		localStorage.removeItem(NOTIFICATION_TOKEN_KEY);
		this.logger.info("FCM token cleared from localStorage");
	}
	/**
	* Get the Firebase Messaging instance for advanced usage
	*/
	getMessaging() {
		return this.messaging;
	}
};
//#endregion
export { FirebaseMessagingProvider, NOTIFICATION_TOKEN_KEY };
